import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RecordOpen } from '../tasks/model.ts';
import { OpenedRecord } from './OpenedRecord.tsx';
import { TimeField } from './tasks/Fields.tsx';
import { timeState } from '../tasks/fields.ts';
import { formatDate } from '../dates.ts';
import { isFirstVisit, recordedMessage, visitMet } from '../siteVisits.ts';
import {
  useRecordSiteVisit, useWithdrawSiteVisit, type PropertyDetail, type SiteVisit, type SiteVisitDeadline,
} from '../api/properties.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { useEditPropertyVisit, usePropertyVisitVersions } from '../api/visits.ts';
import { asEdits, firstOf, ReasonFields, useReasonReady, visitValue } from './VisitDetails.tsx';
import { EarlierVersions, EditedMarker } from './EarlierVersions.tsx';
import { RecordEdit } from './RecordEdit.ts';

const actionLink = 'mt-1 mr-3 min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The property's site visit deadline in words: owed, met (by which visit, how long after), or why closed. */
function deadlineWords(d: SiteVisitDeadline): string {
  if (d.status === 'Complete') {
    return `Met on ${d.completed_on ? formatDate(d.completed_on) : 'a date not known'}`
      + `${d.days_taken !== null ? `, ${d.days_taken} days after the obligation began` : ''}.`;
  }
  if (d.status === 'Upcoming' || d.status === 'In Progress') {
    const late = d.days_until < 0;
    return `${late ? 'Overdue' : 'Due'}: ${formatDate(d.due_date)}${d.is_provisional ? ' (an estimate)' : ''}`
      + ` · ${late ? `${-d.days_until} days late` : `in ${d.days_until} days`}. Owed from ${d.anchor_date ? formatDate(d.anchor_date) : 'a date not known'}.`;
  }
  if (d.status === 'Closed on discharge') return 'Closed on the client\'s discharge: assumed met or waived, pending confirmation.';
  const why: Record<string, string> = {
    property_let_go: 'the property was let go before a visit was recorded',
    tenure_changed: 'the tenure recorded owes no site visit',
    property_retired: 'the property record was retired',
    no_owed_property: 'the client owned or rented no active property',
    lived_in: 'the client lives here, so the home is checked by Social Work\'s welfare visits',
  };
  return `Not owed: ${d.closed_by_event ? why[d.closed_by_event] ?? d.closed_by_event : 'not applicable'}.`;
}

/**
 * The property's initial site visit (122, Arthur, 16 October): owed per property, met by the first visit to it on
 * or after the obligation began. Tiers 1-2 record a visit (date, who visited from the active staff list, a
 * note) and withdraw one recorded in error, with a reason. A withdrawn visit stays listed, marked.
 * Recording is open to whoever may_record_site_visit names (150: the Property team member holding the task too);
 * opened from a site-visit task (A-#191), the form is open from the start.
 */
export function SiteVisits({ detail, mayAct, startRecording = false, showVisit = null }: {
  detail: PropertyDetail; mayAct: boolean; startRecording?: boolean; showVisit?: string | null;
}) {
  const mayRecordVisit = detail.may_record_site_visit;
  const [recording, setRecording] = useState(startRecording && mayRecordVisit);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // A-#194: opened at one visit (from the task it closed, or the Timeline): that visit marked (OpenedRecord, deploy 8)
  const openedFrom = (useLocation().state as RecordOpen | null)?.opened_from ?? null;

  return (
    <div>
      <h3 className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">Site visits</h3>
      {detail.property.occupied && !detail.site_visits.some((d) => d.status === 'Upcoming' || d.status === 'In Progress') && (
        <p className="mt-1 text-neutral-900">The client lives here: checked by Social Work&apos;s welfare visits.</p>
      )}
      {detail.site_visits.length === 0 ? (
        <p className="text-sm text-neutral-600">No initial site visit is owed on this property.</p>
      ) : (
        detail.site_visits.map((d) => (
          <p key={d.id} className="mt-1 text-neutral-900">
            <span className="font-medium">
              {d.property_left_on ? `Site visit after the client left on ${formatDate(d.property_left_on)}.` : 'Initial property site visit.'}
            </span> {deadlineWords(d)}
          </p>
        ))
      )}

      {detail.visits.length > 0 && (
        <ol className="mt-2 divide-y divide-neutral-100">
          {detail.visits.map((v: SiteVisit) => (
            <OpenedRecord key={v.id} id={`site-visit-${v.id}`} opened={v.id === showVisit} from={openedFrom} className="py-2">
              <p className={v.withdrawn ? 'text-neutral-600 line-through' : 'font-medium text-neutral-900'}>
                {formatDate(v.visited_on)} · {v.visited_by ?? 'visitor not recorded'}
                {!v.withdrawn && visitMet(detail.site_visits, v.id) && <span className="font-normal"> · met {visitMet(detail.site_visits, v.id)}</span>}
              </p>
              <p className="text-sm text-neutral-600">
                Recorded by {v.recorded_by ?? 'someone not on record'}, {formatDate(v.created_at.slice(0, 10))}
                {v.documents_retrieved ? ' · Documents were retrieved' : ''}
                {v.note ? ` · ${v.note}` : ''}
              </p>
              {v.withdrawn && (
                <p className="text-sm text-neutral-700">
                  Withdrawn{v.withdrawn_at ? ` ${formatDate(v.withdrawn_at.slice(0, 10))}` : ''}: {v.withdrawal_reason}
                </p>
              )}
              {v.edits > 0 && <SiteVisitHistory id={v.id} />}
              {withdrawing !== v.id && editing !== v.id && (
                <RecordEdit may={mayAct && !v.withdrawn} className={actionLink} choices={[
                  { key: 'correct', label: 'Correct', onPick: () => { setEditing(v.id); setSaid(null); } },
                  { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => { setWithdrawing(v.id); setSaid(null); } },
                ]} />
              )}
              {editing === v.id && (
                <EditSiteVisit detail={detail} visit={v} onDone={(m) => { setEditing(null); setSaid(m); }} />
              )}
              {withdrawing === v.id && (
                <WithdrawVisit propertyId={detail.property.id} visitId={v.id}
                               onDone={(m) => { setWithdrawing(null); setSaid(m); }} />
              )}
            </OpenedRecord>
          ))}
        </ol>
      )}

      {said && <p role="status" className="mt-2 rounded-lg bg-neutral-50 px-3 py-2 text-neutral-800">{said}</p>}
      {mayRecordVisit && !recording && (
        <button type="button" className={`${secondary} mt-2`} onClick={() => { setRecording(true); setSaid(null); }}>
          Record a site visit
        </button>
      )}
      {recording && (
        <RecordVisit detail={detail} onDone={(m) => { setRecording(false); setSaid(m); }} />
      )}
    </div>
  );
}

export function RecordVisit({ detail, onDone }: { detail: PropertyDetail; onDone: (message: string | null) => void }) {
  const [date, setDate] = useState('');
  const [visitor, setVisitor] = useState(detail.visitors.find((v) => v.is_me)?.id ?? '');
  const [note, setNote] = useState('');
  // Time spent (Arthur, A-#83 A7): optional, read by the shared parser, counted in the ledger under Property.
  const [time, setTime] = useState('');
  // Documents retrieved (Arthur, A-#154): unticked unless the recorder ticks it.
  const [documents, setDocuments] = useState(false);
  const spent = timeState(time);
  const record = useRecordSiteVisit(detail.property.id);
  const sending = useRef(false);
  const ready = date !== '' && visitor !== '' && !('error' in spent);
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || sending.current) return;
            sending.current = true;
            record.mutate({ visited_on: date, visited_by_user_id: visitor, ...(note.trim() ? { note: note.trim() } : {}),
              ...('minutes' in spent ? { duration_minutes: spent.minutes } : {}), documents_retrieved: documents }, {
              onSuccess: (r) => onDone(recordedMessage(r.site_visits, r.visit_id)),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Record a site visit</h3>
      <p className={hint}>
        A visit made before today can be recorded, however long ago. The first visit on or after the day the
        obligation began meets the initial site visit; an earlier one is recorded and meets nothing.
      </p>
      <div>
        <label className={label} htmlFor="sv-date">Date of the visit</label>
        <input id="sv-date" type="date" className={field} value={date} max={todayLocal()} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor="sv-visitor">Who visited</label>
        <select id="sv-visitor" className={field} value={visitor} onChange={(e) => setVisitor(e.target.value)}>
          <option value="">Choose…</option>
          {detail.visitors.map((v) => <option key={v.id} value={v.id}>{v.full_name}</option>)}
        </select>
      </div>
      <div>
        <TimeField value={time} onChange={setTime} />
        <p className={hint}>Optional.</p>
      </div>
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
        <input id="sv-documents" type="checkbox" className="mt-1 size-5 shrink-0" checked={documents}
               onChange={(e) => setDocuments(e.target.checked)} />
        <span className="text-neutral-900">Documents were retrieved</span>
      </label>
      {documents && (
        <p className={hint}>Recording a property&rsquo;s first site visit with this ticked gives Finance a task to investigate the documents.</p>
      )}
      <div>
        <label className={label} htmlFor="sv-note">Note <span className="font-normal text-neutral-500">— optional</span></label>
        <textarea id="sv-note" className={`${field} min-h-[72px] py-2`} value={note} maxLength={4000} onChange={(e) => setNote(e.target.value)} />
      </div>
      {record.error && <Refusal error={record.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready || record.isPending}>
          {record.isPending ? 'Recording…' : 'Record the visit'}
        </button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawVisit({ propertyId, visitId, onDone }: { propertyId: string; visitId: string; onDone: (m: string | null) => void }) {
  const [reason, setReason] = useState('');
  const withdraw = useWithdrawSiteVisit(propertyId);
  const sending = useRef(false);
  return (
    <form className="mt-2 space-y-2 rounded-lg border border-neutral-300 p-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!reason.trim() || sending.current) return;
            sending.current = true;
            withdraw.mutate({ visitId, reason: reason.trim() }, {
              onSuccess: () => onDone('Withdrawn. It stays listed, marked, and stops counting.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <p className="text-sm text-neutral-700">
        It stays listed, marked as withdrawn with your reason. If it met the initial site visit, the next visit on
        or after the day the obligation began meets it, or it is owed again.
      </p>
      <label className={label} htmlFor={`sv-why-${visitId}`}>Why is it being withdrawn?</label>
      <textarea id={`sv-why-${visitId}`} className={`${field} min-h-[72px] py-2`} value={reason} maxLength={4000}
                onChange={(e) => setReason(e.target.value)} />
      {withdraw.error && <Refusal error={withdraw.error} />}
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!reason.trim() || withdraw.isPending}>Withdraw it</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

/** An edited site visit: its note is on the row once; the Edited marker, and only its earlier versions (19 October). */
export function SiteVisitHistory({ id }: { id: string }) {
  const versions = usePropertyVisitVersions(id, true);
  const edits = versions.data ? asEdits(versions.data.versions) : undefined;
  return (
    <div className="mt-1">
      <EditedMarker edits={edits} />
      <EarlierVersions edits={edits} first={firstOf(versions.data?.versions)} loading={versions.isLoading}
                       error={versions.error as Error | null} format={visitValue} />
    </div>
  );
}

/** A site visit corrected (125): its date, who visited, its note, and why. A new version; the deadline follows. */
function EditSiteVisit({ detail, visit, onDone }: { detail: PropertyDetail; visit: SiteVisit; onDone: (m: string | null) => void }) {
  const [date, setDate] = useState(visit.visited_on);
  const [visitor, setVisitor] = useState(visit.visited_by_user_id ?? '');
  const [note, setNote] = useState(visit.note ?? '');
  const [documents, setDocuments] = useState(visit.documents_retrieved);
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const save = useEditPropertyVisit(detail.property.id, visit.id);
  const sending = useRef(false);
  const reasonReady = useReasonReady(reason, reasonText);
  const changed = date !== visit.visited_on || visitor !== (visit.visited_by_user_id ?? '') || note.trim() !== (visit.note ?? '').trim()
    || documents !== visit.documents_retrieved;
  const ready = changed && reasonReady && date !== '' && visitor !== '' && !save.isPending;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || sending.current) return;
            sending.current = true;
            save.mutate({ visited_on: date, visited_by_user_id: visitor, note: note.trim() || null,
                          documents_retrieved: documents, reason,
                          ...(reasonText.trim() ? { reason_text: reasonText.trim() } : {}) }, {
              onSuccess: () => onDone('Saved. The visit as it was is kept as an earlier version.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Edit the site visit</h3>
      <p className={hint}>Correct what was recorded. Every change is kept, with your reason; the initial site visit follows the date.</p>
      <div>
        <label className={label} htmlFor={`sve-date-${visit.id}`}>Date of the visit</label>
        <input id={`sve-date-${visit.id}`} type="date" className={field} value={date} max={todayLocal()} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor={`sve-visitor-${visit.id}`}>Who visited</label>
        <select id={`sve-visitor-${visit.id}`} className={field} value={visitor} onChange={(e) => setVisitor(e.target.value)}>
          <option value="">Choose…</option>
          {detail.visitors.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
        </select>
      </div>
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
        <input id={`sve-documents-${visit.id}`} type="checkbox" className="mt-1 size-5 shrink-0" checked={documents}
               onChange={(e) => setDocuments(e.target.checked)} />
        <span className="text-neutral-900">Documents were retrieved</span>
      </label>
      {documents && !visit.documents_retrieved && isFirstVisit(detail.visits, visit) && (
        <p className={hint}>
          Ticking it makes a task for Finance to investigate the documents, unless one was already made for this property.
        </p>
      )}
      <div>
        <label className={label} htmlFor={`sve-note-${visit.id}`}>Note <span className="font-normal text-neutral-500">— optional</span></label>
        <textarea id={`sve-note-${visit.id}`} className={`${field} min-h-[72px] py-2`} value={note} maxLength={4000} onChange={(e) => setNote(e.target.value)} />
      </div>
      <ReasonFields reason={reason} text={reasonText} setReason={setReason} setText={setReasonText} idPrefix={`sve-${visit.id}`} />
      {!changed && <p className={hint}>Nothing has changed yet.</p>}
      {save.error && <Refusal error={save.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready}>{save.isPending ? 'Saving…' : 'Save the change'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}
